import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { IconRegistryProvider } from "@/components/StatusIcon";
import { SiteContentProvider } from "@/context/SiteContentContext";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import PortalPage from "@/pages/PortalPage";
import ApplicationPage from "@/pages/ApplicationPage";
import AdminPage from "@/pages/AdminPage";
import HomePage from "@/pages/HomePage";
import GrowersPage from "@/pages/GrowersPage";
import PublicGrowerPage from "@/pages/PublicGrowerPage";
import TerritoriesPage from "@/pages/TerritoriesPage";
import HowItWorksPage from "@/pages/HowItWorksPage";
import BecomeAGrowerPage from "@/pages/BecomeAGrowerPage";
import AboutPage from "@/pages/AboutPage";
import WaitlistPage from "@/pages/WaitlistPage";
import SubscribePage from "@/pages/SubscribePage";
import CheckoutReturnPage from "@/pages/CheckoutReturnPage";
import SubscriberPage from "@/pages/SubscriberPage";
import GrowerPortalPage from "@/pages/GrowerPortalPage";
import TestLoginPage from "@/pages/TestLoginPage";
import EtcheryPartnershipPage from "@/pages/EtcheryPartnershipPage";

function isSubscriber(user) {
  if (!user) return false;
  if (user.role === "subscriber") return true;
  return Array.isArray(user.roles) && user.roles.includes("subscriber");
}

function SubscriberProtected({ children }) {
  const { user, bootstrapped } = useAuth();
  if (!bootstrapped) {
    return (
      <div className="min-h-screen flex items-center justify-center text-[var(--bloom-ink-muted)] font-serif italic">
        Loading…
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (!isSubscriber(user) && user.role !== "admin") return <Navigate to="/portal" replace />;
  return children;
}

function Protected({ children, adminOnly = false, adminOrEtchery = false }) {
  const { user, bootstrapped } = useAuth();
  if (!bootstrapped) {
    return (
      <div className="min-h-screen flex items-center justify-center text-[var(--bloom-ink-muted)] font-serif italic">
        Loading…
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (adminOrEtchery && user.role !== "admin" && user.role !== "etchery") return <Navigate to="/portal" replace />;
  if (adminOnly && user.role !== "admin") return <Navigate to="/portal" replace />;
  return children;
}

function PublicOnly({ children }) {
  const { user, bootstrapped } = useAuth();
  if (!bootstrapped) return null;
  if (user) {
    let dest = "/portal";
    if (user.role === "admin" || user.role === "etchery") dest = "/admin";
    return <Navigate to={dest} replace />;
  }
  return children;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <IconRegistryProvider>
          <SiteContentProvider>
          <BrowserRouter>
          <Routes>
            {/* Public Bloom Club site */}
            <Route path="/" element={<HomePage />} />
            <Route path="/growers" element={<GrowersPage />} />
            <Route path="/growers/:slug" element={<PublicGrowerPage />} />
            <Route path="/territories" element={<TerritoriesPage />} />
            <Route path="/how-it-works" element={<HowItWorksPage />} />
            <Route path="/become-a-grower" element={<BecomeAGrowerPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/waitlist" element={<WaitlistPage />} />
            <Route path="/subscribe" element={<SubscribePage />} />
            <Route path="/checkout/return" element={<CheckoutReturnPage />} />
            <Route path="/partnership/etchery" element={<EtcheryPartnershipPage />} />

            {/* Auth, always accessible so admins can sign out & register a test grower */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />

            {/* Grower portal */}
            <Route path="/portal" element={<Protected><PortalPage /></Protected>} />
            <Route path="/application" element={<Protected><ApplicationPage /></Protected>} />
            <Route path="/admin" element={<Protected adminOrEtchery><AdminPage /></Protected>} />

            {/* Subscriber portal (Phase C) */}
            <Route path="/subscriber" element={<SubscriberProtected><SubscriberPage /></SubscriberProtected>} />

            {/* Grower portal (Phase D) */}
            <Route path="/grower" element={<Protected><GrowerPortalPage /></Protected>} />

            {/* Test-login bridge (used by admin Test Mode to open impersonated sessions in a new tab) */}
            <Route path="/test-login" element={<TestLoginPage />} />

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          </BrowserRouter>
          </SiteContentProvider>
        </IconRegistryProvider>
      </AuthProvider>
    </div>
  );
}

export default App;
